/**
 * Role hierarchy for Abramov CRM
 *
 * Owner  (system-level, creates offices)
 * └── Office
 *     ├── Admin          (office-level, multiple)
 *     ├── CRM Manager    (office-level)
 *     ├── Sales Dept
 *     │   ├── Head of Sales
 *     │   └── Desks → Teams → [ TL Sales · Sales Agent ]
 *     └── Retention Dept
 *         ├── Head of Retention
 *         └── Desks → Teams → [ TL Ret · Ret Agent ]
 */

// ── Role value constants ──────────────────────────────────────────────────────
export const ROLES = {
  OWNER:           'owner',
  ADMIN:           'admin',
  CRM_MANAGER:     'crm_manager',
  HEAD_OF_SALES:   'head_of_sales',
  HEAD_OF_RET:     'head_of_ret',
  SALES_TEAM:      'sales_team',   // Team Leader, Sales
  RET_TEAM:        'ret_team',     // Team Leader, Retention
  SALES_AGENT:     'sales_agent',
  RET_AGENT:       'ret_agent',
};

// ── Human-readable labels ─────────────────────────────────────────────────────
export const ROLE_LABELS = {
  owner:          'Owner',
  admin:          'Admin',
  crm_manager:    'CRM Manager',
  head_of_sales:  'Head of Sales',
  head_of_ret:    'Head of Retention',
  sales_team:     'TL Sales',
  ret_team:       'TL Retention',
  sales_agent:    'Sales Agent',
  ret_agent:      'Ret Agent',
};

// ── Role groupings ────────────────────────────────────────────────────────────

/** Can manage the entire office (all departments) */
export const PRIVILEGED_ROLES = ['owner', 'admin', 'crm_manager'];

/** Admin department: office-level roles with transaction controls */
export const ADMIN_DEPARTMENT_ROLES = ['owner', 'admin', 'crm_manager'];

/** Belong to the Sales department (any level) */
export const SALES_ROLES = ['head_of_sales', 'sales_team', 'sales_agent'];

/** Belong to the Retention department (any level) */
export const RET_ROLES = ['head_of_ret', 'ret_team', 'ret_agent'];

/** Can see/manage users and settings */
export const MANAGER_ROLES = ['owner', 'admin', 'crm_manager', 'head_of_sales', 'head_of_ret'];

// ── Helper: current user loaded from the backend auth context ─────────────────
let currentUserCache = null;

export function setCurrentUserCache(user) {
  currentUserCache = user && typeof user === 'object' ? user : null;
}

export function clearCurrentUserCache() {
  currentUserCache = null;
}

export function getCurrentUser() {
  return currentUserCache || {};
}

let permissionsCache = null;

export function setPermissionsCache(permissions) {
  permissionsCache = permissions && typeof permissions === 'object' ? permissions : {};
}

export function clearPermissionsCache() {
  permissionsCache = null;
}

export function hasPermission(userOrPermission, maybePermission) {
  const user = maybePermission ? userOrPermission : getCurrentUser();
  const permission = maybePermission || userOrPermission;
  const role = user?.role;

  if (role === 'owner' || role === 'admin') return true;
  if (!role || !permission) return false;

  const saved = permissionsCache?.[role]?.[permission];
  if (typeof saved === 'boolean') return saved;
  return false;
}

export function hasAnyPermission(user, permissions = []) {
  return permissions.some(permission => hasPermission(user, permission));
}

// ── Permission helpers ────────────────────────────────────────────────────────

/** User can edit status/data in the Sales department */
export function canEditSales(role) {
  return PRIVILEGED_ROLES.includes(role) || SALES_ROLES.includes(role);
}

/** User can edit status/data in the Retention department */
export function canEditRetention(role) {
  return PRIVILEGED_ROLES.includes(role) || RET_ROLES.includes(role);
}

/** User can edit BOTH departments (cross-department access) */
export function canEditAnyDept(role) {
  return PRIVILEGED_ROLES.includes(role);
}

/** User can choose withdrawal/status in transaction forms */
export function canUseTransactionAdminControls(role) {
  return ADMIN_DEPARTMENT_ROLES.includes(role);
}

/** User can manage other users (create/edit/delete) */
export function canManageUsers(role) {
  return PRIVILEGED_ROLES.includes(role);
}

/** User can access Settings */
export function canAccessSettings(role) {
  return PRIVILEGED_ROLES.includes(role);
}

/** User can delete comments when granted by the role permission matrix. */
export function canDeleteComment(user = getCurrentUser()) {
  return hasPermission(user, 'Delete Comments');
}

/** owner / admin / crm_manager can edit comments */
export function canEditComment(role) {
  return PRIVILEGED_ROLES.includes(role);
}

/** The department a role naturally belongs to (null = cross-department) */
export function roleDepartment(role) {
  if (SALES_ROLES.includes(role)) return 'sales';
  if (RET_ROLES.includes(role))   return 'retention';
  return null; // owner / admin / crm_manager have no dept restriction
}

export function getResultAccess(user = getCurrentUser()) {
  const role = user?.role;
  const fixedDepartment = ['sales_team', 'ret_team', 'head_of_sales', 'head_of_ret'].includes(role)
    ? roleDepartment(role)
    : null;

  return {
    canView: !['sales_agent', 'ret_agent'].includes(role),
    canSwitchDepartment: !['sales_agent', 'ret_agent'].includes(role) && !fixedDepartment,
    fixedDepartment,
  };
}

/** Ordered list of roles for dropdowns (Owner excluded — set only at system level) */
export const ROLE_OPTIONS = [
  { value: 'admin',         label: 'Admin',            group: 'Office' },
  { value: 'crm_manager',   label: 'CRM Manager',      group: 'Office' },
  { value: 'head_of_sales', label: 'Head of Sales',    group: 'Sales' },
  { value: 'sales_team',    label: 'TL Sales',         group: 'Sales' },
  { value: 'sales_agent',   label: 'Sales Agent',      group: 'Sales' },
  { value: 'head_of_ret',   label: 'Head of Retention',group: 'Retention' },
  { value: 'ret_team',      label: 'TL Retention',     group: 'Retention' },
  { value: 'ret_agent',     label: 'Ret Agent',        group: 'Retention' },
];

/** Badge colours per role */
export const ROLE_COLORS = {
  owner:          'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  admin:          'bg-red-500/20 text-red-400 border-red-500/30',
  crm_manager:    'bg-orange-500/20 text-orange-400 border-orange-500/30',
  head_of_sales:  'bg-green-500/20 text-green-400 border-green-500/30',
  head_of_ret:    'bg-blue-500/20 text-blue-400 border-blue-500/30',
  sales_team:     'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  ret_team:       'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  sales_agent:    'bg-teal-500/20 text-teal-400 border-teal-500/30',
  ret_agent:      'bg-sky-500/20 text-sky-400 border-sky-500/30',
};

let roleConfigCache = null;

export function setRoleConfigCache(config) {
  roleConfigCache = config && typeof config === 'object' ? config : null;
}

export function clearRoleConfigCache() {
  roleConfigCache = null;
}

export function getRoleMetadata({ includeSystem = true } = {}) {
  const roles = Array.isArray(roleConfigCache?.roles) ? roleConfigCache.roles : ROLE_OPTIONS;
  return roles.filter(role => {
    if (!includeSystem && role.system) return false;
    return true;
  });
}

export function getAssignableRoleOptions() {
  return getRoleMetadata({ includeSystem: false });
}

export function getRoleLabels() {
  return Object.fromEntries(getRoleMetadata().map(role => [role.value, role.label]));
}

export function getRoleDepartments() {
  return Object.fromEntries(getRoleMetadata().map(role => [role.value, role.department || null]));
}

export function getDefaultPermissions() {
  return roleConfigCache?.defaultPermissions || null;
}

export function getHeadRoles() {
  return getRoleMetadata().filter(role => role.value?.startsWith('head_of_')).map(role => role.value);
}

export function getTeamRoles() {
  return getRoleMetadata()
    .filter(role => ['sales_team', 'ret_team', 'sales_agent', 'ret_agent'].includes(role.value))
    .map(role => role.value);
}

export function getLeaderRoles() {
  return getRoleMetadata()
    .filter(role => ['sales_team', 'ret_team'].includes(role.value))
    .map(role => role.value);
}
